import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight04Icon, Files02Icon } from '@hugeicons-pro/core-bulk-rounded'
import {
    Archive01Icon,
    Cylinder01Icon,
    FileUploadIcon,
    JoinBevelIcon,
    Pdf01Icon,
    ThreeDViewIcon,
} from '@hugeicons-pro/core-twotone-rounded'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Button, TextField, ThemeProvider, Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/material/Grid'
import { alpha, useTheme } from '@mui/material/styles'
import { useSnackbar } from 'notistack'

import { useGetCustomerQuery } from '@/app/services/customer'
import { useCreateOrderMaterialListMutation } from '@/app/services/materials'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import AddItemCard from '@/common/components/AddItemCards/AddItemCard'
import AppToolbar from '@/common/components/AppBar/AppToolbar'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { theme as darkTheme } from '@/common/themes/DarkTheme'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'

import Add3DPartsDialog from '../order/components/common/Add3DPartsDialog'
import AddFromPartLibrary from '../order/components/common/AddFromPartLibrary'
import AddOrderItemsDialog from '../order/components/common/AddOrderItemsDialog'
import AddPDFPartsDialog from '../order/components/common/AddPDFParts'
import AddQuickPartDialog from '../order/components/common/AddQuickPartDialog'

// 3 cards at AddItemCard's own 460px max width, plus 2 gaps of 24px (Grid spacing={3} ==
// theme.spacing(3)) between them. Capping the row to this width keeps each column exactly
// card-width, so the grid's column gap doesn't get stretched into extra whitespace around a
// narrower centered card — see AddItemCard.jsx `classes.root.maxWidth`.
const CARD_ROW_MAX_WIDTH = 3 * 460 + 2 * 24

const classes = {
    container: {
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: `1px solid ${blueGrey[200]}`,
        borderWidth: 1,
        paddingInline: 3,
        paddingBlock: 4,
        minHeight: 'calc(100vh - 158px)',
    },
    mainContent: {
        maxWidth: '600px',
        gap: 5,
    },

    draftIdContainer: {
        gap: 0.75,
    },
    draftIdInputContainer: {
        gap: 1.5,
    },
    goToDraftButton: {
        flex: '1 0 auto',
    },
    fileListContainer: {
        overflow: 'hidden',
    },
    fileListWrapper: {
        marginBottom: 2,
    },
    filesToUpload: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginBottom: 1.5,
    },
    fileItem: {
        px: 1,
        py: 0.5,
        gap: 1,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderRadius: 0.5,
    },
    smallText: {
        fontSize: '0.75rem',
    },
}

const Home = () => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { i18n, t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showOrderHistory, showTube } = useToolBoxTreatments()

    const { isAuthenticated, login, register } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const paidFeatures = useSelector(selectPaidFeatures)
    const currentCustomerId = useSelector(selectCustomerId)

    const [orderId, setOrderId] = useState('')
    const [interfaceToShow, setInterfaceToShow] = useState(null)
    const [is3DTube, setIs3DTube] = useState(false)

    const isGuestModeEnabled = storeSettings?.webStoreIsPublic
    // `?? true`, not `&&`: QM-3520 (backend) hasn't shipped this field yet, so every current
    // API response omits it — falling back to `&&` would hide 2D uploads and Quick Part for
    // every Web Store the moment this ships, before the backend flag exists. One flag covers
    // both: a Quick Part is a 2D part, so hiding 2D uploads hides Quick Part too.
    const is2dUploadEnabled = storeSettings?.webStore2dEnabled ?? true
    const isPartLibraryAvailable = storeSettings?.webStorePartLibraryEnabled && paidFeatures?.hasPartLibrary
    const isUnfoldTubeEnabled = storeSettings?.webStoreTubeEnabled && paidFeatures?.hasUnfoldTube && showTube
    const is3dEnabled = storeSettings?.webStore3dEnabled && paidFeatures?.has3d
    const isPDFUploadEnabled = storeSettings?.webStorePdfEnabled && paidFeatures?.hasPdfUpload

    const { data: currentCustomer } = useGetCustomerQuery(
        { organisationId, customerId: currentCustomerId },
        { skip: !isAuthenticated && !currentCustomerId }
    )

    const [createOrderMaterialList] = useCreateOrderMaterialListMutation()

    const handleDraftIdChange = (e) => {
        setOrderId(e.target.value)
    }

    const handleGoToDraft = () => {
        if (orderId) {
            navigate(`orders/${orderId}`)
        }
    }

    const createMaterialList = useCallback(
        async (organisationId, urlParams) => {
            try {
                await createOrderMaterialList({ organisationId, location: urlParams }).unwrap()
            } catch (error) {
                console.error(error)
            }
        },
        [createOrderMaterialList]
    )

    const resolvedDarkTheme = useMemo(
        () => (StoreThemeOverride ? StoreThemeOverride(darkTheme, storeSettings) : darkTheme),
        [storeSettings]
    )

    const resetInterface = () => {
        setInterfaceToShow(null)
        setIs3DTube(false)
    }

    const handleAdd2dPartsClick = () => {
        setInterfaceToShow('2d')
    }

    const handleAddFromQuickPartClick = () => {
        setInterfaceToShow('quickpart')
    }

    const handleAdd3dPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Unfold3d')
        setInterfaceToShow('3d-tube')
    }, [organisationId, createMaterialList])

    const handleAddRotaryPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Unfold3d')
        setIs3DTube(true)
        setInterfaceToShow('3d-tube')
    }, [organisationId, createMaterialList])

    const handleAddPdfPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Pdf2Quote')
        setInterfaceToShow('pdf')
    }, [organisationId, createMaterialList])

    const handleAddFromPartsLibraryClick = () => {
        try {
            setInterfaceToShow('partlibrary')
        } catch {
            enqueueSnackbar(t('$t(An error occurred), failed to add parts from the library.'), {
                variant: 'error',
            })
        }
    }

    return (
        <>
            <AppToolbar />
            <Grid
                component="section"
                size={{
                    xs: 12,
                }}
                sx={classes.container}
                container
            >
                <Grid
                    size={{
                        xs: 12,
                    }}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: 1,
                        gap: 6,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            textAlign="center"
                            variant="h2"
                        >
                            {t(storeSettings?.webStoreHeadingParagraph) || t('Welcome to the store')}
                        </Typography>
                        <Typography
                            align="center"
                            variant="h5"
                        >
                            {t(storeSettings?.webStoreSubheadingParagraph) ||
                                t('From upload to purchase in a matter of minutes')}
                        </Typography>
                    </Box>

                    {!isAuthenticated ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 4 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: 2,
                                    paddingTop: 3.5,
                                    flexDirection: 'column',
                                    gap: 2,
                                    width: 300,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Button
                                    color="primary"
                                    size="large"
                                    variant="contained"
                                    fullWidth
                                    onClick={() =>
                                        login({ lang: i18n.language, orgCode: storeSettings?.kindeOrganisationId })
                                    }
                                >
                                    {t('Log in')}
                                </Button>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">{t("Don't have an account?")}</Typography>
                                    <Button
                                        color="secondary"
                                        size="small"
                                        variant="text"
                                        onClick={() =>
                                            register({
                                                lang: i18n.language,
                                                orgCode: storeSettings?.kindeOrganisationId,
                                            })
                                        }
                                    >
                                        {t('Register here')}
                                    </Button>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    width: 500,
                                }}
                            >
                                <form
                                    action={handleGoToDraft}
                                    id="go-to-draft-form"
                                >
                                    <Typography
                                        sx={{ marginBottom: 1 }}
                                        variant="body2"
                                    >
                                        {t("Don't have an account? Continue with an order ID")}
                                    </Typography>

                                    <TextField
                                        helperText={t(
                                            'Enter the order ID you received via email to view or continue your saved order.'
                                        )}
                                        label={t('Order ID')}
                                        size="small"
                                        value={orderId}
                                        variant="outlined"
                                        fullWidth
                                        onChange={handleDraftIdChange}
                                    />
                                </form>

                                <Button
                                    color="secondary"
                                    disabled={!orderId}
                                    endIcon={<HugeiconsIcon icon={ArrowRight04Icon} />}
                                    form="go-to-draft-form"
                                    size="medium"
                                    type="submit"
                                    variant="contained"
                                >
                                    {t('Go to Order')}
                                </Button>
                            </Box>
                        </Box>
                    ) : showOrderHistory ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 3,
                            }}
                        >
                            <Button
                                color="primary"
                                size="medium"
                                startIcon={<HugeiconsIcon icon={Files02Icon} />}
                                variant="contained"
                                onClick={() => navigate('/my-orders')}
                            >
                                {t('Order History')}
                            </Button>

                            <Typography variant="body2">{t('or')}</Typography>
                        </Box>
                    ) : null}
                </Grid>

                {isAuthenticated || isGuestModeEnabled ? (
                    <Grid
                        size={{
                            xs: 12,
                        }}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            width: 1,
                            gap: 3,
                        }}
                    >
                        <Box>
                            {!isAuthenticated && isGuestModeEnabled ? (
                                <Typography
                                    sx={{ marginTop: 4 }}
                                    textAlign="center"
                                    variant="body2"
                                >
                                    {t('or')}
                                </Typography>
                            ) : null}
                            <Typography variant="body1">{t('Select an option to start a new order')}</Typography>
                        </Box>

                        <Grid
                            size={12}
                            spacing={3}
                            sx={{ justifyContent: 'center', maxWidth: CARD_ROW_MAX_WIDTH, mx: 'auto' }}
                            container
                        >
                            {is2dUploadEnabled ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Upload files"
                                        helperText="Upload one or many 2D CAD files to begin your order. Supported formats include DXF and DWG."
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={FileUploadIcon}
                                                size={64}
                                            />
                                        }
                                        title="Upload your DXF or DWG files to get started"
                                        onButtonClick={handleAdd2dPartsClick}
                                    />
                                </Grid>
                            ) : null}

                            {is2dUploadEnabled ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Create a Quick part"
                                        helperText="Quickly create standard parts such as base plates, flanges and bracketry from a library of templates, and customise them to your needs."
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={JoinBevelIcon}
                                                size={64}
                                            />
                                        }
                                        title="Don’t have a CAD file but need something standard?"
                                        onButtonClick={handleAddFromQuickPartClick}
                                    />
                                </Grid>
                            ) : null}

                            {isPartLibraryAvailable ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Browse your part library"
                                        disabled={!isAuthenticated}
                                        disabledMessage="Log in to reorder your parts"
                                        helperText="In your part library, you can find parts from past orders and quickly add them to a new order."
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={Archive01Icon}
                                                size={64}
                                            />
                                        }
                                        title="Want to reorder some parts?"
                                        onButtonClick={handleAddFromPartsLibraryClick}
                                    />
                                </Grid>
                            ) : null}

                            {is3dEnabled ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Unfold 3D models"
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={ThreeDViewIcon}
                                                size={64}
                                            />
                                        }
                                        title="Upload a 3D model and we'll extract the sheet metal components"
                                        onButtonClick={handleAdd3dPartsClick}
                                    />
                                </Grid>
                            ) : null}

                            {isUnfoldTubeEnabled ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Upload tube models"
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={Cylinder01Icon}
                                                size={64}
                                            />
                                        }
                                        title="Upload rolled hollow section tube files"
                                        onButtonClick={handleAddRotaryPartsClick}
                                    />
                                </Grid>
                            ) : null}

                            {isPDFUploadEnabled ? (
                                <Grid
                                    size={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <AddItemCard
                                        buttonText="Extract from PDF"
                                        icon={
                                            <HugeiconsIcon
                                                color={theme.palette.secondary.main}
                                                icon={Pdf01Icon}
                                                size={64}
                                            />
                                        }
                                        title="Upload a vector PDF and select the parts to cut"
                                        onButtonClick={handleAddPdfPartsClick}
                                    />
                                </Grid>
                            ) : null}
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>

            {interfaceToShow === '2d' ? (
                <AddOrderItemsDialog
                    open={interfaceToShow === '2d'}
                    onClose={resetInterface}
                />
            ) : null}

            <ThemeProvider theme={resolvedDarkTheme}>
                {interfaceToShow === 'quickpart' ? (
                    <AddQuickPartDialog
                        open={interfaceToShow === 'quickpart'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === '3d-tube' ? (
                    <Add3DPartsDialog
                        is3DTube={is3DTube}
                        open={interfaceToShow === '3d-tube'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === 'pdf' ? (
                    <AddPDFPartsDialog
                        open={interfaceToShow === 'pdf'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === 'partlibrary' && isAuthenticated && currentCustomer ? (
                    <AddFromPartLibrary
                        open={interfaceToShow === 'partlibrary'}
                        onClose={resetInterface}
                    />
                ) : null}
            </ThemeProvider>
        </>
    )
}

export default Home
